import React from "react";
import { motion } from "framer-motion";
import { Car, Truck, Bike, Zap } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels } from "@/i18n";

const AuctionCategoriesSection = () => {
  const { language } = useLanguage();
  const labels = commonLabels(language);
  const sectionData = labels.menuPages?.buyCar?.auctionCategoriesSection || {};
  const categoriesData = sectionData.categories || [];

  const icons = [Car, Truck, Bike, Zap];
  const colors = [
    "from-blue-500 to-cyan-500",
    "from-green-500 to-emerald-500",
    "from-purple-500 to-pink-500",
    "from-yellow-500 to-orange-500",
  ];
  const backgroundImages = [
    "/lovable-uploads/8dbf9009-6b38-4a2e-a075-c88e8002246b.png",
    "/lovable-uploads/81499cd7-e211-47ae-8027-5c32417a1fdd.png",
    "/lovable-uploads/c03af868-ca6a-4133-a231-9bbd0b2edf36.png",
    "/lovable-uploads/4178f76f-1c76-4558-9298-8d67be1af112.png",
  ];

  const auctionCategories = categoriesData.map(
    (category: any, index: number) => {
      const Icon = icons[index] || icons[0];
      return {
        icon: <Icon className="w-6 h-6 md:w-8 md:h-8" />,
        title: category.title || "",
        description: category.description || "",
        color: colors[index] || colors[0],
        backgroundImage: backgroundImages[index] || backgroundImages[0],
      };
    },
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      className="bg-gray-50 rounded-2xl md:rounded-3xl p-8 md:p-12 border border-gray-200 mb-16 md:mb-20"
    >
      <div className="text-center mb-6 md:mb-8">
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
          {sectionData.title || "Auktionskategorier"}
        </h3>
        <p className="text-gray-600 px-4">
          {sectionData.description ||
            "Välj kategori och börja buda på ditt nästa fordon"}
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 text-center">
        {auctionCategories.map((category, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
            className="relative bg-white rounded-xl md:rounded-2xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300 h-48 md:h-64"
          >
            {/* Background image */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${category.backgroundImage})` }}
            />

            {/* Overlay */}
            <div className="absolute inset-0 bg-black/40" />

            {/* Content */}
            <div className="relative z-10 p-4 md:p-6 h-full flex flex-col justify-between text-white">
              <div
                className={`inline-flex p-2 md:p-3 rounded-full bg-gradient-to-br ${category.color} text-white mb-3 md:mb-4 self-center`}
              >
                {category.icon}
              </div>

              <div>
                <div className="text-base md:text-lg font-semibold mb-1 md:mb-2">
                  {category.title}
                </div>
                <div className="text-xs md:text-sm opacity-90">
                  {category.description}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default AuctionCategoriesSection;
